import { useState, useEffect, useCallback, useRef } from 'react';
import { userAPI } from '../../../../../services/api/userAPI';
import notificationService from '../../../../../services/notificationService';

export const useUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [filters, setFilters] = useState({
    status: 'all', // all, active, blocked
    userType: 'all' // all, client, admin, etc.
  });
  const [refreshKey, setRefreshKey] = useState(0);

  // Ref to track if component is mounted and current request
  const isMounted = useRef(true);
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0); // Track request to ignore stale responses

  const fetchUsers = useCallback(async () => {
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError('');
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchQuery || undefined
      };

      // Add status filter if not 'all'
      if (filters.status !== 'all') {
        params.isBlocked = filters.status === 'blocked';
      }

      // Add user type filter if not 'all'
      if (filters.userType !== 'all') {
        // Map 'client' to 'user' as expected by the backend
        params.role = filters.userType === 'client' ? 'user' : filters.userType;
      }

      // Increment request ID to track this request
      const currentRequestId = ++requestIdRef.current;
      
      const response = await userAPI.getAllUsers(params, {
        signal: abortControllerRef.current.signal
      });
      
      // Ignore stale responses from previous requests
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      
      // Only update state if component is still mounted
      if (isMounted.current) {
        // Handle the raw axios response structure
        if (response.data) {
          // Handle nested response structure
          if (response.data.data) {
            // Check various possible locations for users
            let usersArray = [];
            if (Array.isArray(response.data.data)) {
              usersArray = response.data.data;
            } else if (response.data.data.users) {
              usersArray = response.data.data.users;
            } else if (response.data.data.data) {
              usersArray = response.data.data.data;
            }
            
            setUsers(usersArray);
            
            // Check various possible locations for pagination
            let paginationData = null;
            if (response.data.data.pagination) {
              paginationData = response.data.data.pagination;
            } else if (response.data.data.meta) {
              paginationData = response.data.data.meta;
            } else if (response.data.pagination) {
              paginationData = response.data.pagination;
            }
            
            if (paginationData) {
              setPagination({
                page: paginationData.page || 1,
                limit: paginationData.limit || 20,
                total: paginationData.total || 0,
                pages: paginationData.pages || Math.ceil((paginationData.total || 0) / (paginationData.limit || 20))
              });
            }
          } else {
            // Fallback for direct structure
            const usersArray = response.data.users || response.data || [];
            setUsers(usersArray);
            if (response.data.pagination) {
              setPagination(response.data.pagination);
            }
          }
        } else {
          setUsers([]);
        }
      }
    } catch (err) {
      // Only update state if component is still mounted and error wasn't aborted
      if (isMounted.current && err.name !== 'AbortError') {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to load users';
        setError(errorMessage);
        setUsers([]);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [pagination.page, pagination.limit, searchQuery, filters, refreshKey]);

  useEffect(() => {
    fetchUsers();

    // Cleanup function to cancel requests when component unmounts
    return () => {
      // Only abort the request, don't set isMounted to false
      // This allows subsequent requests to still update state
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchUsers]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  const handleFilterChange = useCallback((filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    // Scroll to top of list when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleRefresh = useCallback(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    setRefreshKey(k => k + 1);
  }, []);

  const blockUser = useCallback(async (userId) => {
    if (!userId) {
      notificationService.error('User ID is required to block a user');
      return false;
    }

    try {
      const result = await userAPI.blockUser(userId);
      
      if (result.data && (result.data.success !== false)) {
        notificationService.success('User blocked successfully');
        // Optimistic update - update the user in the list immediately
        setUsers(prevUsers => 
          prevUsers.map(user => 
            (user.id === userId || user.accountId === userId || user._id === userId) 
              ? { ...user, isBlocked: true } 
              : user
          )
        );
        // Then refresh to ensure consistency
        await fetchUsers();
        return true;
      } else {
        notificationService.error(result.data?.message || 'Failed to block user');
        return false;
      }
    } catch (err) {
      console.error('Failed to block user:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to block user';
      notificationService.error(errorMessage);
      return false;
    }
  }, [fetchUsers]);

  const unblockUser = useCallback(async (userId) => {
    if (!userId) {
      notificationService.error('User ID is required to unblock a user');
      return false;
    }

    try {
      const result = await userAPI.unblockUser(userId);
      
      if (result.data && (result.data.success !== false)) {
        notificationService.success('User unblocked successfully');
        // Optimistic update - update the user in the list immediately
        setUsers(prevUsers => 
          prevUsers.map(user => 
            (user.id === userId || user.accountId === userId || user._id === userId) 
              ? { ...user, isBlocked: false } 
              : user
          )
        );
        // Then refresh to ensure consistency
        await fetchUsers();
        return true;
      } else {
        notificationService.error(result.data?.message || 'Failed to unblock user');
        return false;
      }
    } catch (err) {
      console.error('Failed to unblock user:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to unblock user';
      notificationService.error(errorMessage);
      return false;
    }
  }, [fetchUsers]);

  const deleteUser = useCallback(async (userId, reason = 'Admin action') => {
    if (!userId) {
      notificationService.error('User ID is required to delete a user');
      return false;
    }

    try {
      const result = await userAPI.deleteUser(userId, reason);
      
      // Check for success in the response structure
      const success = result.data?.data?.success || result.data?.success !== false;
      
      if (success) {
        const message = result.data?.data?.data?.message || result.data?.data?.message || 'User deleted successfully';
        notificationService.success(message);
        
        // Remove the user from the list immediately
        setUsers(prevUsers => 
          prevUsers.filter(user => 
            (user.id !== userId && user.accountId !== userId && user._id !== userId)
          )
        );
        
        // Then refresh to ensure consistency
        await fetchUsers();
        return true;
      } else {
        const errorMsg = result.data?.data?.error || result.data?.error || result.data?.message || 'Failed to delete user';
        notificationService.error(errorMsg);
        return false;
      }
    } catch (err) {
      console.error('Failed to delete user:', err);
      // Handle both Axios errors and plain Error objects (from handleApiError)
      const errorMessage = err.response?.data?.data?.error ||
                          err.response?.data?.error ||
                          err.response?.data?.message ||
                          err.message ||
                          'Failed to delete user';
      notificationService.error(errorMessage);
      return false;
    }
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    searchQuery,
    pagination,
    filters,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleRefresh,
    blockUser,
    unblockUser,
    deleteUser
  };
};
